'use client';

import React from 'react';
import classNames from 'classnames';
import WistiaVideoEmbed from './WistiaVideo/WistiaVideoEmbed';
import WistiaVideoBase from './WistiaVideo/WistiaVideoBase';
import BrightcoveEmbed from './Brightcove/BrightcoveEmbed';
import AnchorLink from '@/components/TableOfContents/AnchorLink';
import Button from '@/components/Button/Button';
import ContentfulRichText from '@/components/ContentfulRichText/ContentfulRichText';
import AnimatedLink from '@/components/AnimatedLink/AnimatedLink';
import { analytics } from '@/utils/analytics';
import { bgColorMap } from '@/utils/styleHelpers';
import { DescriptionType } from '@/utils/common.types';

interface Props {
  alignment: string;
  anchorTagRef: string;
  buttonLabel: string;
  buttonOpenNewWindow: boolean;
  buttonUrl: string;
  buttonStyle: string;
  description: DescriptionType;
  includeCardShadow?: boolean;
  includeSegment?: boolean;
  backgroundColorTw?: string;
  isStoryBook?: boolean;
  isWidescreen?: boolean;
  title: string;
  video: {
    id: string;
    host: string;
  };
}

const VideoCard = ({
  title,
  description,
  buttonLabel,
  buttonUrl,
  buttonStyle,
  buttonOpenNewWindow,
  video,
  alignment,
  anchorTagRef,
  isWidescreen,
  isStoryBook,
  includeCardShadow,
  includeSegment,
  backgroundColorTw,
}: Props) => {
  return (
    <section
      className={classNames('mx-auto my-[57px] w-mobile md:max-w-standard', {
        ['shadow-component']: includeCardShadow,
        [bgColorMap(backgroundColorTw)]: backgroundColorTw,
      })}
    >
      <AnchorLink anchorTagRef={anchorTagRef} />
      <section id={anchorTagRef && `anchortag-${anchorTagRef}`}>
        <div
          className={classNames(
            'grid-cols-1 grid-rows-1 gap-[18px_18px] md:grid-rows-[auto]',
            `${alignment === 'left' ? 'md:grid-cols-[1.25fr_1fr]' : 'md:grid-cols-[1fr_1.25fr]'}`,
            `${isWidescreen ? 'block' : 'grid'}`
          )}
        >
          <div
            className={classNames(
              'col-start-1 row-start-1 m-0 w-full self-center p-0',
              `${alignment === 'left' ? 'md:my-0 md:ml-[18px] md:mr-0' : 'md:m-0'}`,
              { ['md:col-start-2']: alignment !== 'left' }
            )}
          >
            <h3 className="m-0 mx-[30px] mb-2.5 mt-4 font-acumin text-[25px] font-bold leading-[26px] tracking-[-0.35px] text-hbg-blue md:hidden">
              {title}
            </h3>
            <div className="md:w-[95%] md:*:my-[5%]">
              {video?.host === 'wistia' && (
                <WistiaVideoEmbed title={title} videoId={video?.id} />
              )}
              {video?.host === 'brightcove' && (
                <BrightcoveEmbed videoId={video?.id} />
              )}
            </div>
          </div>
          <div
            className={classNames(
              'col-start-1 row-start-2 mx-auto my-[25px] w-mobile self-center md:row-start-1 md:mt-5',
              { ['md:col-start-2']: alignment === 'left' }
            )}
          >
            <h3
              className={classNames(
                'm-0 hidden font-acumin text-[2rem] font-bold leading-[1.2] tracking-[-0.35px] text-hbg-blue',
                `${isWidescreen ? 'pr-5 pt-2.5 md:block' : 'md:inline'}`
              )}
            >
              {title}
            </h3>
            <div
              className={classNames(
                'desc',
                'pb-5 pt-[27px] *:break-words *:font-source-sans *:text-[1rem] *:leading-[1.69] *:text-hbg-blue smDesktop:*:text-[1.15rem]',
                '[&:not(:first-of-type)]:pt-5',
                'split-content',
                `${isWidescreen ? 'md:py-2.5' : 'md:pb-5 md:pt-[27px]'}`
              )}
            >
              {description && ContentfulRichText(description)}
            </div>
            {buttonUrl &&
              (buttonStyle === 'arrow' ? (
                <AnimatedLink
                  title={buttonLabel}
                  buttonUrl={buttonUrl}
                  buttonOpenNewWindow={buttonOpenNewWindow}
                  onClick={() =>
                    includeSegment &&
                    analytics?.track('CTA Clicked', {
                      isNav: true,
                      type: 'text',
                      text: buttonLabel,
                    })
                  }
                />
              ) : (
                <Button
                  label={buttonLabel}
                  href={buttonUrl}
                  openNewWindow={buttonOpenNewWindow}
                  onClick={() =>
                    includeSegment &&
                    analytics?.track('CTA Clicked', {
                      isNav: true,
                      type: 'button',
                      text: buttonLabel,
                    })
                  }
                />
              ))}
          </div>
        </div>
      </section>
      {/* Storybook requires the base video script. */}
      {isStoryBook && <WistiaVideoBase />}
    </section>
  );
};

export default VideoCard;
